import {
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"

import "./loadingIntro.scss"
import useCoachOnboardingStore, {
  CoachOnboardingStep,
} from "./useCoachOnboardingStore"
import LinearProgress from "../../components/linearProgress/LinearProgress"

const LOADER_DURATION_MS = 2500
const VALUE_PROP_INTERVAL_MS = 850

// TODO(content): swap these for marketing-approved copy before launch.
const VALUE_PROPS = [
  "Personalized to your child's needs",
  "Built with behavioral-therapy experts",
  "Most parents match with a coach in under 2 minutes",
  "Trusted by 30,000+ Joon families",
]

// TODO(assets): swap text-logo placeholders for real outlet logos
// (Forbes / Today / NPR / etc.) when assets are dropped in /images/press.
const FEATURED_IN = ["Forbes", "TODAY", "Parents", "Romper"]

const INNER_DOT_COUNT = 8
const OUTER_DOT_COUNT = 16
const INNER_RADIUS = 38
const OUTER_RADIUS = 92

const LoadingIntro = () => {
  const setStep = useCoachOnboardingStore((s) => s.setStep)
  const [valuePropIndex, setValuePropIndex] = useState(0)
  const [progress, setProgress] = useState(0)

  // Hold the 2.5s before advancing to QUIZ_OR_SKIP. The sessionStorage
  // flag is set so a back-nav within the same tab doesn't re-trigger the
  // loader — that gate lives in CoachOnboarding.tsx on mount.
  useEffect(() => {
    const advance = setTimeout(() => {
      try {
        sessionStorage.setItem("coachOnboardingLoaderShown", "true")
      } catch {
        // sessionStorage may be unavailable (private mode, etc.) — non-fatal.
      }
      setStep(CoachOnboardingStep.QUIZ_OR_SKIP)
    }, LOADER_DURATION_MS)
    return () => clearTimeout(advance)
  }, [setStep])

  // Progress bar fills smoothly over the loader duration.
  useEffect(() => {
    const start = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const pct = Math.min(100, ((now - start) / LOADER_DURATION_MS) * 100)
      setProgress(pct)
      if (pct < 100) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  // Rotate the value-prop strip.
  useEffect(() => {
    const interval = setInterval(() => {
      setValuePropIndex((i) => (i + 1) % VALUE_PROPS.length)
    }, VALUE_PROP_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  return (
    <FlexBox
      direction="column"
      align="center"
      justify="flex-start"
      wrap={false}
      gap={SPACING.space6}
      style={{
        width: "min(450px, 100%)",
        margin: "0 auto",
        padding: `${SPACING.space6} ${SPACING.space4} 12dvh`,
      }}
    >
      <Typography
        variant="h2Serif"
        textAlign="center"
        style={{ marginTop: SPACING.space4 }}
      >
        Analyzing your needs
      </Typography>
      <Typography
        variant="bodySmall"
        textAlign="center"
        color={JoonUIColor.text.secondary}
      >
        Pulling together the right Joon coaches for your family.
      </Typography>

      <DotConstellation />

      <FlexBox
        direction="column"
        align="center"
        justify="center"
        wrap={false}
        style={{ minHeight: 32, width: "100%" }}
      >
        <Typography
          key={valuePropIndex}
          variant="bodyBold"
          textAlign="center"
          color={JoonUIColor.text.primary}
        >
          <span className="joon-loading-intro__value-prop">
            {VALUE_PROPS[valuePropIndex]}
          </span>
        </Typography>
      </FlexBox>

      <ParentQuoteCard />

      <FeaturedInStrip />

      <FlexBox direction="column" align="center" wrap={false} fullWidth>
        <Typography
          variant="bodySmall"
          textAlign="center"
          color={JoonUIColor.text.secondary}
          style={{ marginBottom: SPACING.space2 }}
        >
          Please don&apos;t close this page
        </Typography>
        <LinearProgress
          value={progress}
          colorPrimary={JoonColorExpanded.viridian300}
          style={{ width: "100%" }}
        />
      </FlexBox>
    </FlexBox>
  )
}

const DotConstellation = () => {
  return (
    <div
      style={{
        position: "relative",
        width: 220,
        height: 220,
        margin: `${SPACING.space2} 0`,
      }}
      aria-hidden="true"
    >
      {Array.from({ length: INNER_DOT_COUNT }).map((_, i) => {
        const angle = (i / INNER_DOT_COUNT) * Math.PI * 2
        const x = 110 + Math.cos(angle) * INNER_RADIUS - 5
        const y = 110 + Math.sin(angle) * INNER_RADIUS - 5
        return (
          <span
            key={`inner-${i}`}
            className="joon-loading-intro__dot joon-loading-intro__dot--inner"
            style={{
              left: x,
              top: y,
              animationDelay: `${(i / INNER_DOT_COUNT) * 0.8}s`,
            }}
          />
        )
      })}
      {Array.from({ length: OUTER_DOT_COUNT }).map((_, i) => {
        const angle = (i / OUTER_DOT_COUNT) * Math.PI * 2
        const x = 110 + Math.cos(angle) * OUTER_RADIUS - 6
        const y = 110 + Math.sin(angle) * OUTER_RADIUS - 6
        return (
          <span
            key={`outer-${i}`}
            className="joon-loading-intro__dot"
            style={{
              left: x,
              top: y,
              animationDelay: `${0.4 + (i / OUTER_DOT_COUNT) * 1.2}s`,
            }}
          />
        )
      })}
    </div>
  )
}

const ParentQuoteCard = () => {
  return (
    <FlexBox
      direction="column"
      align="center"
      wrap={false}
      gap={SPACING.space2}
      style={{
        background: "rgba(255, 255, 255, 0.65)",
        borderRadius: SPACING.space2,
        padding: SPACING.space4,
        border: `1px solid ${JoonColorExpanded.neutral200}`,
        width: "100%",
      }}
    >
      <Typography
        variant="bodySmall"
        textAlign="center"
        color={JoonUIColor.text.primary}
        style={{ fontStyle: "italic" }}
      >
        {/* TODO(content): swap for marketing-approved testimonial */}
        &ldquo;Within two weeks, our morning meltdowns were down by half.
        Our coach gave us a plan that actually worked.&rdquo;
      </Typography>
      <Typography
        variant="bodyXSmall"
        textAlign="center"
        color={JoonUIColor.text.secondary}
      >
        — Sarah M., parent of a 7-year-old
      </Typography>
    </FlexBox>
  )
}

const FeaturedInStrip = () => {
  return (
    <FlexBox
      direction="column"
      align="center"
      wrap={false}
      gap={SPACING.space2}
      fullWidth
    >
      <Typography
        variant="bodyXSmall"
        textAlign="center"
        color={JoonUIColor.text.secondary}
        style={{ letterSpacing: 1.2, textTransform: "uppercase" }}
      >
        As featured in
      </Typography>
      <FlexBox
        direction="row"
        align="center"
        justify="center"
        gap={SPACING.space4}
        wrap={true}
        style={{ width: "100%" }}
      >
        {FEATURED_IN.map((outlet) => (
          <Typography
            key={outlet}
            variant="bodyBold"
            color={JoonUIColor.text.secondary}
            style={{
              opacity: 0.7,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            {outlet}
          </Typography>
        ))}
      </FlexBox>
    </FlexBox>
  )
}

export default LoadingIntro
